/**
 * wrapperRegistry.js — WRAPPER-DISCIPLINE-EXT-1
 *
 * Canonical registry of all governed*Call files in the codebase.
 * Frozen at load time; no runtime mutation permitted.
 *
 * Two-tier model:
 *
 *   Tier A — SDK Wrappers (canonical 3)
 *     The only files permitted to gate direct Anthropic SDK access.
 *     Any NEW file in this tier requires Cognitive Council convening,
 *     constitutional review, and operator + senior architect approval.
 *     Default answer to "can we add a 4th SDK wrapper?" is NO.
 *
 *   Tier B — Routing Shims
 *     Client-side HTTP wrappers that route to Cloud Function endpoints
 *     which internally delegate to a Tier A wrapper. They contain NO
 *     Anthropic SDK imports and are NOT governed wrappers in the SDK-
 *     access-control sense. They are registered here for visibility and
 *     to prevent false-positive alerts from the 4th-wrapper detector.
 *     New Tier B files must declare their tier explicitly in this registry
 *     (see wrapper-extension-protocol.md).
 *
 * The "no 4th wrapper" governance rule targets TIER A only.
 * Tier B routing shims are exempt but tracked.
 *
 * Wired into:
 *   - src/services/governance/wrapperDisciplineEnforcer.js (runtime + CI)
 *   - scripts/check-wrapper-discipline.mjs (CI / pre-commit)
 *   - docs/governance/wrapper-discipline.md (canonical binding rule)
 */

export const WRAPPER_REGISTRY = Object.freeze({

  // ─── Tier A: SDK Wrappers (canonical 3) ─────────────────────────────────────

  governedAegisCall: Object.freeze({
    tier: 'sdk-wrapper',
    directSdk: true,
    path: 'src/services/aegis/governedAegisCall.js',
    purpose: 'Platform substrate — operational AI calls (KAUDIT, ARGUS, AEGIS reply intelligence, EXEC seats, etc.)',
    consumers: 'EXEC seats, KAUDIT, ARGUS, KFOL, KTRUST, AEGIS modules',
    forbiddenConsumers: 'ARTEMIS revenue seats (use governedArtemisCall instead)',
    cfEndpoint: 'VITE_AEGIS_GOVERNED_CALL_URL',
    requiredFields: ['agentId', 'module', 'decisionType', 'tenantId'],
    returnShape: 'AegisDecision + enforcerVerdict + kcritVerdict + finalDisposition',
  }),

  governedArtemisCall: Object.freeze({
    tier: 'sdk-wrapper',
    directSdk: true,
    path: 'src/services/artemis/governedArtemisCall.js',
    purpose: 'Revenue substrate — ARTEMIS constellation calls (voice/narrative/anchors/flywheel patterns)',
    consumers: 'ARTEMIS-001..N constellations',
    forbiddenConsumers: 'EXEC seats, platform substrate',
    cfEndpoint: 'VITE_ARTEMIS_GOVERNED_CALL_URL',
    requiredFields: ['agentId', 'module', 'decisionType', 'tenantId'],
    returnShape: 'AegisDecision + artemisMetadata + enforcerVerdict + kcritVerdict + finalDisposition',
  }),

  governedCouncilCall: Object.freeze({
    tier: 'sdk-wrapper',
    directSdk: true,
    path: 'src/services/cog-council/governedCouncilCall.js',
    purpose: 'Council deliberation substrate — KCC multi-round deliberation (IP → Steelman → Devil → Bayesian → PreMortem → Materiality → Synthesis)',
    consumers: 'COG-COUNCIL-* council types, KCRIT-0 invocations from EXEC seats',
    forbiddenConsumers: 'Single-call AI invocations (those go through governedAegisCall)',
    cfEndpoint: 'VITE_COUNCIL_GOVERNED_CALL_URL',
    requiredFields: ['module', 'decisionType', 'tenantId', 'question'],
    returnShape: 'CouncilResult { sessionId, materialityBand, councilRounds[], synthesis }',
  }),

  // ─── Tier B: Routing Shims ───────────────────────────────────────────────────
  // These files follow the governed*Call naming convention but contain NO
  // Anthropic SDK imports. They are HTTP clients that route to CF endpoints
  // which internally delegate to a Tier A wrapper. They are exempt from the
  // "no 4th SDK wrapper" rule but are registered here for full visibility.

  governedExecCall: Object.freeze({
    tier: 'routing-shim',
    directSdk: false,
    path: 'src/services/exec/governedExecCall.js',
    purpose: 'EXEC-0 client routing shim — routes EXEC council agent calls to the governedExecCall Cloud Function, which delegates to governedAegisCall() server-side',
    consumers: 'Executive Council agent components',
    delegatesTo: 'governedAegisCall (server-side via CF)',
    cfEndpoint: 'EXEC_GOVERNED_CALL_URL_VAR (from execConstants.js)',
    requiredFields: ['agentId', 'module', 'decisionType', 'tenantId'],
    note: 'Added SETTINGS-HUB-1B-EXT: reads agent_temperament_configs and injects temperament params',
  }),

  governedKpolCall: Object.freeze({
    tier: 'routing-shim',
    directSdk: false,
    path: 'src/services/kpol/governedKpolCall.js',
    purpose: 'PKG-FUSION-1 / KPOL substrate — routes KPOL packaging-intelligence calls to the governedKpolCallEndpoint Cloud Function, which delegates to governedAegisCall() server-side',
    consumers: 'KPOL service (packaging exception narrative, packaging decision types)',
    delegatesTo: 'governedAegisCall (server-side via CF)',
    cfEndpoint: 'VITE_KPOL_GOVERNED_CALL_URL',
    requiredFields: ['tenantId', 'module', 'decisionType'],
    note: 'module must be "kpol"; BINDING #25 in source: only legitimate path for LLM calls in src/services/kpol/',
  }),

  governedDesignCriticCall: Object.freeze({
    tier: 'routing-shim',
    directSdk: false,
    path: 'src/aegis/design-critic/governedDesignCriticCall.js',
    purpose: 'DESIGN-CRITIC-1 substrate — provider routing shim for UI/UX design review agent. Routes to governedAegisCall() with provider fallback chain (Opus 4.7 → Sonnet 4.6 → GPT-4o). Pre-deploy + post-deploy modes per EC-1 §2.15.',
    consumers: 'src/aegis/design-critic/index.js (reviewSurface), scripts/design-critic.mjs (CLI)',
    delegatesTo: 'governedAegisCall (client-side call; CF routing per governedAegisCall pattern)',
    requiredFields: ['tenantId', 'mode', 'surface'],
    note: 'DESIGN-CRITIC-1 session (2026-05-20); SERIAL-MERGE-DOCTRINE-1 substrate seat; FOUNDATION-PURITY-1 compliant (read-only)',
  }),

  governedTrustLatticeCall: Object.freeze({
    tier: 'routing-shim',
    directSdk: false,
    path: 'src/modules/intl/services/governedTrustLatticeCall.js',
    purpose: 'INTL-FSMA-204-NATIVE-1 substrate — INTL-scoped wrapper that writes FSMA-204 KDE attestations to the Trust Lattice via TrustLatticeService.logDecision and emits the canonical kde-event-trust-lattice-write-requested EventBus event (terminal action: kde-event-write-to-trust-lattice).',
    consumers: 'src/modules/intl/screens/IntlFsma204StubPanel.jsx (native FSMA-204 KDE capture)',
    delegatesTo: 'TrustLatticeService.logDecision (IOT-TRUST-LATTICE-1 substrate, no Anthropic SDK)',
    requiredFields: ['tenantId', 'cte', 'signalSource'],
    note: 'INTL-FSMA-204-NATIVE-1 session (2026-05-21); SERIAL-MERGE-DOCTRINE-1 narrow carve-out (single platform-global edit required by wrapper-discipline registration doctrine); follows DESIGN-CRITIC-1 precedent for module-substrate-class governed*Call registration.',
  }),

});

/**
 * Convenience: Tier A entries only (SDK wrappers — subject to "no 4th" rule).
 */
export const TIER_A_WRAPPERS = Object.freeze(
  Object.fromEntries(
    Object.entries(WRAPPER_REGISTRY).filter(([, v]) => v.tier === 'sdk-wrapper')
  )
);

/**
 * Convenience: Tier B entries only (routing shims — exempt from "no 4th" rule).
 */
export const TIER_B_SHIMS = Object.freeze(
  Object.fromEntries(
    Object.entries(WRAPPER_REGISTRY).filter(([, v]) => v.tier === 'routing-shim')
  )
);

/**
 * All registered paths (Tier A + Tier B). Used by the enforcer to distinguish
 * "known" from "unknown" governed*Call files.
 */
export const ALL_REGISTERED_PATHS = Object.freeze(
  Object.values(WRAPPER_REGISTRY).map(w => w.path)
);

/**
 * The Tier A SDK wrapper rule. Quoted in CI output and governance docs.
 *
 * Adding a 4th Tier A wrapper requires:
 *   1. Cognitive Council convening
 *   2. Constitutional review
 *   3. Operator + Senior Architect explicit approval
 *   4. Documentation in master plan + this registry
 *
 * Default answer is NO. The 3 SDK wrappers cover all current and foreseeable
 * governed-AI surfaces:
 *   - Operational AI:        governedAegisCall
 *   - Revenue/Sales/Mktg/CS: governedArtemisCall
 *   - Multi-agent deliberation: governedCouncilCall
 *
 * If a use case seems to need a 4th SDK wrapper, the answer is almost always:
 *   (a) It belongs in one of the 3 existing wrappers (most common), OR
 *   (b) It belongs in a new namespace/subsystem inside an existing wrapper
 *       (e.g., governedAegisCall({ subsystem: 'kaudit' })), OR
 *   (c) It's not a governance-needing surface at all (deterministic logic,
 *       no AI call) — don't wrap it.
 *
 * If a new HTTP routing shim is needed (Tier B), it requires:
 *   1. Registration in WRAPPER_REGISTRY with tier: 'routing-shim', directSdk: false
 *   2. KAUDIT-7 forward-mode entry in docs/governance/decisions.md
 *   3. The Cloud Function it routes to must delegate to an existing Tier A wrapper
 *
 * See docs/governance/wrapper-extension-protocol.md for the full decision tree.
 */
export const FORBIDDEN_4TH_TIER_A_WRAPPER_RULE = `
The 3 Tier A SDK wrappers (governedAegisCall, governedArtemisCall, governedCouncilCall)
are the ONLY files permitted to gate Anthropic SDK access.

Adding a 4th Tier A wrapper requires Cognitive Council convening, constitutional review,
and operator + senior architect approval. Default answer is NO.

See docs/governance/wrapper-discipline.md for the full rule.
`;
